import { Button, Center, Loader } from "@mantine/core";
import { AppModal } from "@mountaya/studio-react";
import { IconCalendarCheckOutline18 } from "nucleo-ui-outline-18";
import { lazy, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";

import { ICON_SIZE } from "@mountayaapp/theme";

import "./DocsBookDemo.css";

/** CAL_LINK is the Mountaya booking page the demo modal opens, shared with the marketing site. */
const CAL_LINK = "mountaya/onboarding";

/** CAL_EMBED_JS is the EU Cal.com embed runtime the booking calendar loads from. */
const CAL_EMBED_JS = "https://app.cal.eu/embed/embed.js";

/**
 * BOOK_DEMO_WIDTH is the dialog's width: wide enough for Cal's month view to lay out
 * its day grid beside the slot column without the embed scrolling horizontally.
 */
const BOOK_DEMO_WIDTH = "62rem";

// The Cal embed touches window and ships a heavy calendar, so it loads only on the
// client when the modal first opens — never during the static prerender.
const Cal = lazy(() => import("@calcom/embed-react"));

/**
 * DocsBookDemo is the header's "Book a demo" call to action: a ghost button that
 * opens the Cal.com booking calendar inside the shared dialog shell. Hosting Cal's
 * inline embed in our own modal (rather than Cal's popup) keeps the chrome on our
 * stack, so the modal follows the docs color scheme instead of opening as a white box
 * in dark mode; a cropping wrapper trims Cal's footer logo the way the marketing site
 * does. The embed itself uses the marketing site's exact configuration.
 *
 * It composes the same AppModal every dialog in the app does, so the docs' one dialog
 * carries the shared header band, the calendar glyph its own trigger wears, a header
 * line saying what booking gets you, and the single filled Close an info-only dialog
 * ends with — rather than being the one surface outside the convention.
 */
export const DocsBookDemo = () => {
  const { t } = useTranslation(["docs", "common"]);
  const [opened, setOpened] = useState(false);

  const close = () => setOpened(false);

  return (
    <>
      <Button
        leftSection={<IconCalendarCheckOutline18 size={ICON_SIZE.sm} />}
        onClick={() => setOpened(true)}
        variant="default"
      >
        {t("nav.bookDemo")}
      </Button>

      <AppModal
        closeLabel={t("close", { ns: "common" })}
        description={t("nav.bookDemoDescription")}
        icon={<IconCalendarCheckOutline18 size={ICON_SIZE.md} />}
        onClose={close}
        opened={opened}
        size={BOOK_DEMO_WIDTH}
        title={t("nav.bookDemo")}
      >
        <div className="docs-book-demo-cal-crop">
          {opened ? (
            <Suspense
              fallback={
                <Center mih={400}>
                  <Loader />
                </Center>
              }
            >
              <Cal
                calLink={CAL_LINK}
                className="docs-book-demo-cal"
                config={{ layout: "month_view", useSlotsViewOnSmallScreen: "true" }}
                embedJsUrl={CAL_EMBED_JS}
              />
            </Suspense>
          ) : null}
        </div>
      </AppModal>
    </>
  );
};
